import { DataProvider, CrudFilter, LogicalFilter } from "@refinedev/core";
import axios from "axios";
import { apiService } from "./api";
import { Content, Actor, UserManagement, Notification, ContentFilters } from "@/types";

// Fonction pour convertir les filtres Refine vers ContentFilters
function convertRefineFiltersToContentFilters(filters?: CrudFilter[]): ContentFilters {
  if (!filters || filters.length === 0) return {};
  
  const contentFilters: ContentFilters = {};
  
  filters.forEach(filter => {
    if (filter && "field" in filter && "value" in filter) {
      const logicalFilter = filter as LogicalFilter;
      switch (logicalFilter.field) {
        case "search":
          contentFilters.search = logicalFilter.value as string;
          break;
        case "type":
          contentFilters.type = logicalFilter.value as number;
          break;
        case "genre":
          contentFilters.genre = logicalFilter.value as number;
          break;
        case "status":
          contentFilters.status = logicalFilter.value as string;
          break;
        case "dateFrom":
          contentFilters.dateFrom = logicalFilter.value as string;
          break;
        case "dateTo":
          contentFilters.dateTo = logicalFilter.value as string;
          break;
        case "tags":
          contentFilters.tags = Array.isArray(logicalFilter.value) ? logicalFilter.value : [logicalFilter.value];
          break;
      }
    }
  });
  
  return contentFilters;
}

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const current = pagination?.current || 1;
    const pageSize = pagination?.pageSize || 20;

    try {
      let response;

      switch (resource) {
        case "contents":
          // Convertir les filtres Refine vers le format attendu par l'API
          const contentFilters = convertRefineFiltersToContentFilters(filters);
          response = await apiService.getContents(
            contentFilters,
            current,
            pageSize
          );
          break;
        case "actors":
          const searchFilter = filters?.find((f) => "field" in f && f.field === "search") as LogicalFilter | undefined;
          const search = searchFilter?.value as string;
          response = await apiService.getActors(current, pageSize, search);
          break;
        case "users": {
          // Les filtres etaient construits par la page puis ignores ici : la
          // recherche d'utilisateur n'avait aucun effet.
          const valeurFiltre = (champ: string): string | undefined => {
            const f = filters?.find((x) => "field" in x && x.field === champ) as
              | LogicalFilter
              | undefined;
            const v = f?.value;
            return v === null || v === undefined || v === "" ? undefined : String(v);
          };
          response = await apiService.getUsers(
            current,
            pageSize,
            valeurFiltre("search"),
            valeurFiltre("population"),
            valeurFiltre("role"),
            valeurFiltre("status")
          );
          break;
        }
        case "notifications":
          response = await apiService.getNotifications(
            filters as any,
            current,
            pageSize
          );
          break;
        case "genres":
          response = await apiService.getGenres();
          return { data: response, total: response.length };
        case "types":
          response = await apiService.getContentTypes();
          return { data: response, total: response.length };
        case "plans":
          response = await apiService.getPlans();
          return { data: response, total: response.length };
        case "paymentMethods":
          response = await apiService.getPaymentMethods();
          return { data: response, total: response.length };
        default:
          throw new Error(`Resource ${resource} not supported`);
      }

      return {
        data: (response.data || response) as any[],
        total: response.total || (Array.isArray(response) ? response.length : 0),
      };
    } catch (error) {
      console.error(`Error fetching ${resource}:`, error);
      throw error;
    }
  },

  getOne: async ({ resource, id, meta }) => {
    try {
      let response;

      switch (resource) {
        case "contents":
          response = await apiService.getContent(Number(id));
          break;
        case "actors":
          response = await apiService.getActor(Number(id));
          break;
        case "users":
          response = await apiService.getUser(String(id));
          break;
        default:
          throw new Error(`Resource ${resource} not supported`);
      }

      return { data: response as any };
    } catch (error) {
      console.error(`Error fetching ${resource} ${id}:`, error);
      throw error;
    }
  },

  create: async ({ resource, variables, meta }) => {
    try {
      let response;

      switch (resource) {
        case "contents":
          response = await apiService.createContent(
            variables as Partial<Content>
          );
          break;
        case "actors":
          response = await apiService.createActor(variables as Partial<Actor>);
          break;
        case "notifications":
          response = await apiService.createNotification(
            variables as Partial<Notification>
          );
          break;
        case "genres":
          response = await apiService.createGenre(variables);
          break;
        case "types":
          response = await apiService.createType(variables);
          break;
        case "plans":
          response = await apiService.createPlan(variables);
          break;
        case "paymentMethods":
          response = await apiService.createPaymentMethod(variables);
          break;
        default:
          throw new Error(`Resource ${resource} not supported`);
      }

      return { data: response as any };
    } catch (error) {
      console.error(`Error creating ${resource}:`, error);
      throw error;
    }
  },

  update: async ({ resource, id, variables, meta }) => {
    try {
      let response;

      switch (resource) {
        case "contents":
          response = await apiService.updateContent(
            Number(id),
            variables as Partial<Content>
          );
          break;
        case "actors":
          response = await apiService.updateActor(
            Number(id),
            variables as Partial<Actor>
          );
          break;
        case "users":
          response = await apiService.updateUser(
            String(id),
            variables as Partial<UserManagement>
          );
          break;
        case "notifications":
          response = await apiService.updateNotification(
            Number(id),
            variables as Partial<Notification>
          );
          break;
        case "genres":
          response = await apiService.updateGenre(Number(id), variables);
          break;
        case "types":
          response = await apiService.updateType(Number(id), variables);
          break;
        case "plans":
          response = await apiService.updatePlan(Number(id), variables);
          break;
        case "paymentMethods":
          response = await apiService.updatePaymentMethod(Number(id), variables);
          break;
        default:
          throw new Error(`Resource ${resource} not supported`);
      }

      return { data: response as any };
    } catch (error) {
      console.error(`Error updating ${resource} ${id}:`, error);
      throw error;
    }
  },

  deleteOne: async ({ resource, id, meta }) => {
    try {
      switch (resource) {
        case "contents":
          await apiService.deleteContent(Number(id));
          break;
        case "actors":
          await apiService.deleteActor(Number(id));
          break;
        case "users":
          await apiService.deleteUser(String(id));
          break;
        case "notifications":
          await apiService.deleteNotification(Number(id));
          break;
        case "genres":
          await apiService.deleteGenre(Number(id));
          break;
        case "types":
          await apiService.deleteType(Number(id));
          break;
        case "plans":
          await apiService.deletePlan(Number(id));
          break;
        case "paymentMethods":
          await apiService.deletePaymentMethod(Number(id));
          break;
        default:
          throw new Error(`Resource ${resource} not supported`);
      }

      return { data: { id } as any };
    } catch (error) {
      console.error(`Error deleting ${resource} ${id}:`, error);
      throw error;
    }
  },

  getApiUrl: () => {
    return "/api";
  },

  custom: async ({
    url,
    method,
    filters,
    sorters,
    payload,
    query,
    headers,
    meta,
  }) => {
    try {
      let config: any = {
        url,
        method,
        data: payload,
        params: query,
        headers,
      };

      // Ajouter les filtres et tri si nécessaire
      if (filters) {
        config.params = { ...config.params, ...filters };
      }

      // Utiliser axios directement pour les appels personnalisés
      const response = await axios(config);
      return { data: response.data };
    } catch (error) {
      console.error("Custom API call error:", error);
      throw error;
    }
  },
};

export default dataProvider;
